import { useState, useEffect, useCallback, Suspense } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  EventHeader,
  EventDetails,
  ClientInfo,
  UnifiedPaymentSection,
  VendorPaymentsSection,
  DetailedItemAllocations,
  ClientPaymentModal,
  AddServiceModal,
  AddVendorModal,
  EventActivityTimeline,
  GiveVendorPaymentModal,
  RoomBookingsSection,
  AddRoomBookingModal,
  EditPaymentModal,
  TaskList,
  ExpenseList,
  EventInvoices,
} from "../components";
import EditEventModal from "../components/EditEventModal";
import ExpenseModal from "../../expenses/ExpenseModal";
import { EventDetail, Task, ClientPayment, ServiceItem, VendorItem } from "../components/types";
import { eventsApi, clientPaymentsApi } from "@/lib/api";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { Loader } from "lucide-react";

function EventDetailPageContent() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const navigate = useNavigate();
  // Allowed next statuses, from the server (same rules the events list uses)
  const [transitions, setTransitions] = useState<Record<string, string[]>>({});
  // One confirmation dialog for removals and cancelling (instead of the browser's confirm box)
  const [confirming, setConfirming] = useState<{ title: string; message: string; action: () => Promise<void> } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const isAdmin = ["super_admin", "admin", "master"].includes(user?.role ?? "");

  const eventId = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("id")
    : null;

  const [event, setEvent] = useState<EventDetail | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [customerPayments, setCustomerPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isClientPaymentModalOpen, setIsClientPaymentModalOpen] = useState(false);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  const [isVendorPaymentModalOpen, setIsVendorPaymentModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ClientPayment | null>(null);

  // Key that VendorPaymentsSection watches to trigger its own re-fetch
  const [vendorPaymentsRefreshKey, setVendorPaymentsRefreshKey] = useState(0);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadPayments = useCallback(async (evId: number) => {
    try {
      const res = await clientPaymentsApi.list({ event_id: evId });
      setCustomerPayments(res.data?.data || res.data || []);
    } catch {
      // keep existing state
    }
  }, []);

  const loadLogs = useCallback(async (evId: number) => {
    try {
      const res = await eventsApi.getLogs(evId);
      setLogs(res.data?.data || res.data || []);
    } catch {
      // the timeline is secondary; keep what we have
    }
  }, []);

  const loadEventData = useCallback(async () => {
    if (!eventId) {
      setError("No event ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await eventsApi.get(parseInt(eventId));
      const eventData: EventDetail = response.data?.data || response.data;
      if (response.status_transitions) setTransitions(response.status_transitions);

      if (!eventData?.id) {
        setError("Event not found");
        setEvent(null);
        return;
      }
      if (eventData.id !== parseInt(eventId)) {
        setError("Event ID mismatch");
        setEvent(null);
        return;
      }

      setEvent(eventData);

      // Tasks, logs and payments are independent — load them together
      const [tasksResult] = await Promise.allSettled([
        eventsApi.getTasks(eventData.id),
        loadLogs(eventData.id),
        loadPayments(eventData.id),
      ]);

      if (tasksResult.status === "fulfilled" && tasksResult.value) {
        setTasks(tasksResult.value.data?.data || tasksResult.value.data || []);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load event details";
      setError(message);
      showToast(message, "error");
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, showToast, loadPayments, loadLogs]);

  useEffect(() => {
    loadEventData();
  }, [loadEventData]);

  // ── Targeted refresh helpers ─────────────────────────────────────────────────

  /** Re-fetch the event (and its activity log) without flickering the page. */
  const refreshEvent = useCallback(async () => {
    if (!eventId) return;
    try {
      const response = await eventsApi.get(parseInt(eventId));
      const eventData: EventDetail = response.data?.data || response.data;
      if (eventData?.id) {
        setEvent(eventData);
        loadLogs(eventData.id);
      }
    } catch {
      // non-fatal — page still has previous state
    }
  }, [eventId, loadLogs]);

  /** Re-fetch client payments only. */
  const refreshPayments = useCallback(async () => {
    if (!event?.id) return;
    await loadPayments(event.id);
    loadLogs(event.id);
  }, [event?.id, loadPayments, loadLogs]);

  /** Trigger VendorPaymentsSection to re-fetch its own data. */
  const refreshVendorPayments = () => setVendorPaymentsRefreshKey((k) => k + 1);

  // ── Derived financial summary for EventHeader ────────────────────────────────

  // Straight from the server's financials, so the header matches the events list:
  // total incl. GST, paid, still due, and profit = revenue after discount − all costs
  const financialSummary = event?.financials
    ? {
        total: Number(event.financials.final_revenue) || 0,
        paid: Number(event.financials.customer?.paid) || 0,
        balance: Number(event.financials.customer?.balance) || 0,
        profit: Number(event.financials.profit) || 0,
      }
    : undefined;

  // ── Event handlers ───────────────────────────────────────────────────────────

  const changeStatus = async (status: string) => {
    if (!event) return;
    try {
      await eventsApi.updateStatus(event.id, status);
      showToast("Status updated", "success");
      await refreshEvent();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to update status", "error");
    }
  };

  const handleStatusChange = async (status: string) => {
    if (status === "cancelled") {
      setConfirming({
        title: "Cancel this event?",
        message: "Booked rooms are released, the invoice is marked cancelled and vendors are no longer owed anything beyond what was already paid. Payments already taken or made are kept. You can restore the event later.",
        action: () => changeStatus("cancelled"),
      });
      return;
    }
    await changeStatus(status);
  };

  const handleDeleteItem = async (item: ServiceItem | VendorItem, type: "service" | "vendor") => {
    if (!event) return;
    if (!item.id) {
      showToast("This item can't be removed here — reload the page and try again", "error");
      return;
    }
    const label = type === "service" ? item.name : `${item.name} (${item.service})`;
    const itemId = item.id;
    setConfirming({
      title: `Remove ${type === "service" ? "service" : "vendor"}?`,
      message: `"${label}" will be removed from this event and the totals recalculated.`,
      action: async () => {
        try {
          if (type === "service") {
            await eventsApi.removeEventService(event.id, itemId);
          } else {
            await eventsApi.removeEventVendor(event.id, itemId);
            refreshVendorPayments();
          }
          showToast(`${type === "service" ? "Service" : "Vendor"} removed`, "success");
          await refreshEvent();
        } catch (err) {
          showToast(err instanceof Error ? err.message : `Failed to remove ${type}`, "error");
        }
      },
    });
  };

  const handleVendorPaymentFromSection = (vendor: any) => {
    setSelectedVendor({
      vendor_id: vendor.vendor_id,
      vendor_name: vendor.vendor_name,
      total_amount: vendor.total_amount,
      paid_amount: vendor.paid_amount,
      balance: vendor.balance,
      items: vendor.items,
      payments: vendor.payments,
    });
    setIsVendorPaymentModalOpen(true);
  };

  // ── Loading / error states ───────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !event) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-600 font-medium mb-4">{error || "Event not found"}</p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => window.history.back()}
                className="min-h-10 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => loadEventData()}
                className="min-h-10 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const eventSummary = {
    id: event.id,
    name: event.name,
    start_date: event.start_date,
    venue: event.venue,
    client: event.client,
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  // Mobile shows: header → event details & client → items/payments → tasks, invoices, expenses, activity.
  // From lg up the details sit in a right-hand column beside the items/payments.

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-4 lg:space-y-6">
        <EventHeader
          event={event}
          financials={financialSummary}
          showProfit={isAdmin}
          onEdit={() => setIsEditOpen(true)}
          onStatusChange={isStaff ? undefined : handleStatusChange}
          statusOptions={transitions[event.status]}
          onAddPayment={() => setIsClientPaymentModalOpen(true)}
          onAddService={() => setIsAddServiceModalOpen(true)}
          onAddVendor={() => setIsAddVendorModalOpen(true)}
          onAddRoom={() => setIsAddRoomModalOpen(true)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-[auto_1fr] gap-4 lg:gap-6">
          {/* ── Summary (first on mobile, top of the right column on desktop) ── */}
          <div className="space-y-4 lg:space-y-6 lg:col-start-3 lg:row-start-1">
            <EventDetails
              event={event}
              tasksCount={tasks.length}
              invoicesCount={event.invoices?.length || 0}
            />
            {event.client && <ClientInfo client={event.client} />}
          </div>

          {/* ── Main column ── */}
          <div className="min-w-0 space-y-4 lg:space-y-6 lg:col-span-2 lg:col-start-1 lg:row-start-1 lg:row-span-2">
            {event.financials && (
              <DetailedItemAllocations
                services={event.financials.services || []}
                vendors={event.financials.vendors || []}
                rooms={event.financials.rooms || []}
                onAddService={() => setIsAddServiceModalOpen(true)}
                onAddVendor={() => setIsAddVendorModalOpen(true)}
                onAddRoom={() => setIsAddRoomModalOpen(true)}
                onDeleteItem={isStaff ? undefined : handleDeleteItem}
              />
            )}

            <RoomBookingsSection
              eventId={event.id}
              rooms={event.financials?.rooms || []}
              onAdd={() => setIsAddRoomModalOpen(true)}
              onDelete={async (id) => {
                setConfirming({
                  title: "Remove this room booking?",
                  message: "The room is freed and its charge comes off the event total.",
                  action: async () => {
                    try {
                      await eventsApi.removeRoomBooking(id);
                      showToast("Room booking removed", "success");
                      await refreshEvent();
                    } catch (err) {
                      showToast(err instanceof Error ? err.message : "Failed to remove room booking", "error");
                    }
                  },
                });
              }}
            />

            <VendorPaymentsSection
              eventId={event.id}
              vendors={event.financials?.vendors || []}
              onPayClick={() => showToast("Select a vendor to make a payment", "info")}
              onPayVendor={handleVendorPaymentFromSection}
              refreshKey={vendorPaymentsRefreshKey}
            />

            {event.financials && (
              <UnifiedPaymentSection
                customer={event.financials.customer}
                payments={customerPayments.length > 0 ? customerPayments : event.clientPayments}
                onAddPayment={() => setIsClientPaymentModalOpen(true)}
                canEditPayments={isAdmin}
                onEditPayment={(payment) => setEditingPayment(payment)}
                event={eventSummary}
              />
            )}
          </div>

          {/* ── Secondary (last on mobile, below the summary on desktop) ── */}
          <div className="space-y-4 lg:space-y-6 lg:col-start-3 lg:row-start-2 lg:self-start">
            <TaskList tasks={tasks} onAddTask={() => navigate(`/tasks?new=1&event_id=${event.id}`)} />
            <EventInvoices invoices={event.invoices || []} />
            <ExpenseList expenses={event.financials?.expenses} onAddExpense={() => setIsExpenseModalOpen(true)} />
            <EventActivityTimeline logs={logs} />
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <ExpenseModal
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        defaultEventId={event.id}
        onExpenseSaved={refreshEvent}
      />

      {isEditOpen && (
        <EditEventModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onEventUpdated={refreshEvent}
          event={{
            ...event,
            client: event.client ?? null,
            package: event.package ?? null,
            venue: event.venue ? { ...event.venue, location: event.venue.location ?? "" } : null,
            end_date: event.end_date ?? null,
          }}
        />
      )}

      {editingPayment && (
        <EditPaymentModal
          isOpen={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          payment={editingPayment}
          onUpdated={async () => {
            setEditingPayment(null);
            await refreshPayments();
          }}
        />
      )}

      {isClientPaymentModalOpen && (
        <ClientPaymentModal
          isOpen={isClientPaymentModalOpen}
          onClose={() => setIsClientPaymentModalOpen(false)}
          eventId={event.id}
          customer={event.financials?.customer}
          payments={event.clientPayments || []}
          onPaymentAdded={async () => {
            setIsClientPaymentModalOpen(false);
            await Promise.all([refreshPayments(), refreshEvent()]);
          }}
          event={eventSummary}
        />
      )}

      {isAddServiceModalOpen && (
        <AddServiceModal
          isOpen={isAddServiceModalOpen}
          onClose={() => setIsAddServiceModalOpen(false)}
          eventId={event.id}
          guestCount={Number(event.guest_count) || 0}
          onServiceAdded={async () => {
            setIsAddServiceModalOpen(false);
            await refreshEvent();
          }}
        />
      )}

      {isAddVendorModalOpen && (
        <AddVendorModal
          isOpen={isAddVendorModalOpen}
          onClose={() => setIsAddVendorModalOpen(false)}
          eventId={event.id}
          onVendorAdded={async () => {
            setIsAddVendorModalOpen(false);
            await refreshEvent();
            refreshVendorPayments();
          }}
        />
      )}

      <AddRoomBookingModal
        isOpen={isAddRoomModalOpen}
        onClose={() => setIsAddRoomModalOpen(false)}
        eventId={event.id}
        onBookingAdded={async () => {
          setIsAddRoomModalOpen(false);
          await refreshEvent();
        }}
      />

      {isVendorPaymentModalOpen && selectedVendor && (
        <GiveVendorPaymentModal
          isOpen={isVendorPaymentModalOpen}
          onClose={() => {
            setIsVendorPaymentModalOpen(false);
            setSelectedVendor(null);
          }}
          eventId={event.id}
          vendor={selectedVendor}
          onPaymentAdded={async () => {
            setIsVendorPaymentModalOpen(false);
            setSelectedVendor(null);
            refreshVendorPayments();
            loadLogs(event.id);
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={confirming !== null}
        onClose={() => { if (!confirmBusy) setConfirming(null); }}
        onConfirm={async () => {
          if (!confirming) return;
          setConfirmBusy(true);
          try {
            await confirming.action();
          } finally {
            setConfirmBusy(false);
            setConfirming(null);
          }
        }}
        title={confirming?.title}
        message={confirming?.message}
        loading={confirmBusy}
      />
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    </DashboardLayout>
  );
}

export default function EventDetailPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <EventDetailPageContent />
    </Suspense>
  );
}
